// One write intent = one `request_id` (erp-core fix4: charges, extend and
// move-room are idempotent per (business, request_id)). The id is minted ONCE,
// when the clerk confirms, and reused by every retry of the SAME body — a lost
// response, a timeout or a 5xx leaves it in place, so the second press answers
// the first result instead of acting again. It is dropped when the server
// answers 2xx (the intent is done) or a definitive 4xx (the row is failed or
// the id was used for another body: the next press is a new intent).
// `409 request_pending` is the same intent still running: wait a moment with
// the SAME id (the mutation stays pending, so the screen's busy guard holds),
// and after the last wait hand the server's «الطلب قيد التنفيذ…» to the caller
// with the id kept.

import { useCallback, useRef } from 'react';
import { isApiError } from '@/lib/api';

/** How long and how often a `request_pending` is waited out before the caller sees it (tests shorten it). */
export const intentPolicy = { pollMs: 1500, polls: 3 };

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** `send(requestId)` posts the write; `fields` is everything the write carries except the id (it names the intent). */
export function useIntentRequest() {
  const ids = useRef(new Map<string, string>());
  return useCallback(async <T,>(fields: unknown, send: (requestId: string) => Promise<T>): Promise<T> => {
    const key = JSON.stringify(fields);
    let id = ids.current.get(key);
    if (!id) {
      id = crypto.randomUUID();
      ids.current.set(key, id);
    }
    for (let waited = 0; ; waited++) {
      try {
        const done = await send(id);
        ids.current.delete(key);
        return done;
      } catch (e) {
        if (isApiError(e) && e.code === 'request_pending') {
          if (waited < intentPolicy.polls) {
            await sleep(intentPolicy.pollMs);
            continue;
          }
        } else if (isApiError(e) && e.status >= 400 && e.status < 500) {
          ids.current.delete(key);
        }
        throw e;
      }
    }
  }, []);
}
